import { RegularModal } from "../modals/modals";
import { Hours } from "@/dto/hours";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useEffect, useId, useState } from "react";
import pom from "@/utils/pom";
import { useLocalizedDays } from "@/utils/useLocalizedDays";
import { convertTimeStringToDate } from "@/utils/dateTimeUtils";

interface BusinessHoursModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  businessHours: Hours;
  timezone: string;
  isRestaurantMerchant?: boolean;
}

export const BusinessHoursModal = ({
  isOpen,
  onRequestClose,
  businessHours,
  timezone,
  isRestaurantMerchant,
}: BusinessHoursModalProps) => {
  const t = useTranslations("BusinessHoursModal");
  const locale = useLocale();
  const format = useFormatter();
  const subtitleId = useId();

  const [currentDay, setCurrentDay] = useState<string>("");
  const modalHeaderText = isRestaurantMerchant
    ? t("orderingHours")
    : t("businessHours");

  const localizedDays = useLocalizedDays();

  useEffect(() => {
    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    setCurrentDay(today);
  }, [isOpen]);

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
      aria={{
        labelledby: pom.accessibilityIds.businessHours,
        describedby: subtitleId,
      }}
      closeButtonClassName=""
      className="top-[initial] sm:top-0"
      contentClassName="bg-white sm:rounded-lg shadow-lg fixed bottom-0 sm:relative w-[100%] sm:w-[425px] mx-auto overflow-hidden sm:max-h-96 flex flex-col no-scrollbar top-[initial] max-h-[80vh]"
    >
      <h2
        className="pt-10 text-2xl px-6 leading-9 sm:text-4xl sm:text-[32px] font-medium text-[#373737]"
        id={pom.accessibilityIds.businessHours}
        aria-describedby={subtitleId}
      >
        {modalHeaderText}
      </h2>
      <ol
        className="flex-1 overflow-y-auto min-h-0 py-4 px-6 list-none flex flex-col items-start no-scrollbar"
        aria-labelledby={pom.accessibilityIds.businessHours}
        id={subtitleId}
      >
        {businessHours &&
          localizedDays.map((day) => {
            const dayHours = businessHours[day.key as keyof Hours] || [];
            const isCurrentDay = currentDay === day.key;

            return (
              <li
                key={day.key}
                className="flex flex-row justify-between w-full"
              >
                <span
                  className={`capitalize ${
                    isCurrentDay ? "font-medium" : "text-[#727272]"
                  }`}
                >
                  <span className="sr-only">{`${day.label},`}</span>
                  <span aria-hidden={true}>{day.label}</span>
                </span>
                <div className="flex flex-col text-end">
                  {dayHours.length > 0 ? (
                    dayHours.map((timeSlot, index) => {
                      const startLuxon = convertTimeStringToDate(
                        timeSlot.start,
                        timezone
                      );
                      const endLuxon = convertTimeStringToDate(
                        timeSlot.end,
                        timezone
                      );

                      const visualStartTime = format.dateTime(
                        startLuxon.toJSDate(),
                        {
                          hour: "numeric",
                          minute: "2-digit",
                          timeZone: timezone,
                        }
                      );

                      const visualEndTime = format.dateTime(
                        endLuxon.toJSDate(),
                        {
                          hour: "numeric",
                          minute: "2-digit",
                          timeZone: timezone,
                        }
                      );

                      const timezoneLabel =
                        startLuxon.setLocale(locale).offsetNameShort;

                      const visualTime = `${visualStartTime} – ${visualEndTime} ${timezoneLabel}`;

                      return (
                        <span
                          key={day.key + index}
                          className={`pb-4 ${
                            isCurrentDay ? "font-medium" : "text-[#727272]"
                          }`}
                        >
                          <span>{visualTime}</span>
                        </span>
                      );
                    })
                  ) : (
                    <>
                      <span className="sr-only">{`${t("closed")}.`}</span>
                      <span aria-hidden={true} className="pb-4 text-[#727272]">
                        {t("closed")}
                      </span>
                    </>
                  )}
                </div>
              </li>
            );
          })}
      </ol>
    </RegularModal>
  );
};
